import { composeValidators, getTitleValidator, titleValidation } from 'validators';
import { recurrencePeriodLabels, routes } from "../../../constants";
import { serviceTemplateModel } from "../../../models";
import { Accounting } from "../../../types/api.model";
import { utils } from "../../../utils";
export let ColumnId = /*#__PURE__*/function (ColumnId) {
  ColumnId["Name"] = "Service name";
  ColumnId["IncludedServices"] = "includedServices";
  ColumnId["BillingOccurance"] = "billingOccurance";
  ColumnId["BillingTrigger"] = "billingTrigger";
  ColumnId["Rate"] = "rate";
  ColumnId["QboStatus"] = "qboStatus";
  return ColumnId;
}({});
export const defaultSort = {
  colIndex: 0,
  sort: 'desc'
};
export const sortIds = headerItems => headerItems.map(item => item.id);
export const bulkImportRoute = entry => ({
  id: "import-".concat(entry),
  text: "Import ".concat(entry),
  url: routes.importServices,
  shouldShow: _ref => {
    let {
      isVendor
    } = _ref;
    return isVendor;
  }
});
export async function saveServiceTemplate(_ref2) {
  let {
    listener,
    modifiedService,
    originalService,
    repo,
    storePopulator,
    descriptionObj
  } = _ref2;
  listener.saveStarted();
  try {
    if (originalService) {
      await repo.update(modifiedService, descriptionObj);
    } else {
      await repo.create(modifiedService);
    }
    await storePopulator.populateServiceTemplates();
  } catch (err) {
    listener.saveFailed();
    throw err;
  } finally {
    listener.saveFinished();
  }
}
function sortByName(a, b) {
  const aName = a.name.toLowerCase();
  const bName = b.name.toLowerCase();
  return aName < bName ? -1 : aName > bName ? 1 : 0;
}
function sortByIncludedServices(a, b) {
  var _a$bundle$services$ma, _a$bundle, _b$bundle$services$ma, _b$bundle;
  // TODO: Fix this impl.
  const aServices = (_a$bundle$services$ma = (_a$bundle = a.bundle) === null || _a$bundle === void 0 ? void 0 : _a$bundle.services.map(_ref3 => {
    let {
      name
    } = _ref3;
    return name;
  }).join(',').toLowerCase()) !== null && _a$bundle$services$ma !== void 0 ? _a$bundle$services$ma : '';
  const bServices = (_b$bundle$services$ma = (_b$bundle = b.bundle) === null || _b$bundle === void 0 ? void 0 : _b$bundle.services.map(_ref4 => {
    let {
      name
    } = _ref4;
    return name;
  }).join(',').toLowerCase()) !== null && _b$bundle$services$ma !== void 0 ? _b$bundle$services$ma : '';
  return aServices < bServices ? -1 : aServices > bServices ? 1 : 0;
}
function sortByPrice(a, b) {
  const [aPrice, bPrice] = [a, b].map(service => utils.applyDiscount(serviceTemplateModel.isFixedPrice(service.cost) || serviceTemplateModel.isVariablePrice(service.cost) ? service.cost.price : service.cost.minPrice != null ? service.cost.minPrice : service.cost.maxPrice, service.cost.discount));
  return aPrice.minus(bPrice).toNumber();
}
function sortByFrequency(a, b) {
  const [aFrequency, bFrequency] = [a, b].map(service => serviceTemplateModel.isRecurring(service.billing) ? service.billing.recurrencePeriod : 'one');
  return aFrequency < bFrequency ? -1 : aFrequency > bFrequency ? 1 : 0;
}
function sortByBilling(a, b) {
  const [aBilling, bBilling] = [a, b].map(service => serviceTemplateModel.isManual(service.billing) ? 'manual' : 'automatic');
  return aBilling < bBilling ? -1 : aBilling > bBilling ? 1 : 0;
}
function sortByQboStatus(a, b, qboItemsById) {
  var _qboItemsById, _qboItemsById2;
  if (!serviceTemplateModel.integratedId({
    serviceTemplate: a,
    accounting: Accounting.QBO
  })) return serviceTemplateModel.integratedId({
    serviceTemplate: b,
    accounting: Accounting.QBO
  }) ? 1 : 0;
  if (!serviceTemplateModel.integratedId({
    serviceTemplate: b,
    accounting: Accounting.QBO
  })) return -1;
  const aName = (_qboItemsById = qboItemsById[serviceTemplateModel.integratedId({
    serviceTemplate: a,
    accounting: Accounting.QBO
  })]) === null || _qboItemsById === void 0 ? void 0 : _qboItemsById.name.toLowerCase();
  const bName = (_qboItemsById2 = qboItemsById[serviceTemplateModel.integratedId({
    serviceTemplate: b,
    accounting: Accounting.QBO
  })]) === null || _qboItemsById2 === void 0 ? void 0 : _qboItemsById2.name.toLowerCase();
  return aName < bName ? -1 : aName > bName ? 1 : 0;
}
export const sortFunctions = {
  [ColumnId.Name]: isAsc => (a, b) => (isAsc ? -1 : 1) * sortByName(a, b),
  [ColumnId.Rate]: isAsc => (a, b) => (isAsc ? -1 : 1) * sortByPrice(a, b) || sortByName(a, b),
  [ColumnId.BillingOccurance]: isAsc => (a, b) => (isAsc ? -1 : 1) * sortByFrequency(a, b) || sortByName(a, b),
  [ColumnId.BillingTrigger]: isAsc => (a, b) => (isAsc ? -1 : 1) * sortByBilling(a, b) || sortByName(a, b),
  [ColumnId.IncludedServices]: isAsc => (a, b) => (isAsc ? -1 : 1) * sortByIncludedServices(a, b),
  [ColumnId.QboStatus]: (isAsc, qboItemsById) => (a, b) => (isAsc ? -1 : 1) * sortByQboStatus(a, b, qboItemsById) || sortByName(a, b)
};
function isUniqueServiceName(_ref5) {
  let {
    serviceName,
    skipServiceId,
    allServices
  } = _ref5;
  return !allServices || allServices.every(service => service.name !== serviceName || service.id === skipServiceId);
}
function validateServiceName(_ref6) {
  let {
    serviceName,
    skipServiceId,
    allServices
  } = _ref6;
  if (!serviceName) {
    return 'No service name';
  }
  if (!isUniqueServiceName({
    serviceName,
    allServices,
    skipServiceId
  })) {
    return 'Provide a unique service name';
  }
  return true;
}
export function getServiceNameValidation(_ref7) {
  let {
    skipServiceId,
    allServices
  } = _ref7;
  return composeValidators([titleValidation('Service name', {
    required: 'Please enter a service name'
  }), {
    validate: {
      serviceName: _serviceName => validateServiceName({
        serviceName: _serviceName,
        skipServiceId,
        allServices
      })
    }
  }]);
}
export function getDuplicatedService(_ref8) {
  let {
    service,
    serviceNames
  } = _ref8;
  const suffix = ' new';
  const newService = serviceTemplateModel.clone(service);
  const titleValidator = getTitleValidator();
  const getIsValid = () => titleValidator(newService.name) === true;
  const getIsNotUnique = () => serviceNames.some(name => name === newService.name);

  // Verify uniqueness and keep adding 'new's until a unique name is received.
  do {
    newService.name += suffix;
  } while (getIsNotUnique());

  // Truncate the name if it is too long.
  let isValid = getIsValid();
  let isNotUnique = getIsNotUnique();
  while (!isValid || isNotUnique) {
    const match = newService.name.match(new RegExp(suffix));
    const suffixIndex = match === null || match === void 0 ? void 0 : match.index;
    if (suffixIndex == null) throw new Error('Suffix not found in service name');
    // Remove last char before ' new' suffix.
    newService.name = newService.name.slice(0, suffixIndex - 1) + newService.name.slice(suffixIndex);
    isValid = getIsValid();
    isNotUnique = getIsNotUnique();
  }
  return newService;
}
export function getDuplicatedServices(services, allServices) {
  const duplicatedServices = [];
  services.forEach(service => {
    const serviceNames = duplicatedServices.concat(allServices || []).map(s => s.name);
    duplicatedServices.push(getDuplicatedService({
      service,
      serviceNames
    }));
  });
  return duplicatedServices;
}
export function billingTriggerLabel(billing) {
  if (serviceTemplateModel.isOneTimeInStages(billing)) {
    return billing.oneTimeInStages.isAutoFirst() ? 'First stage is automatically billed' : 'Manual';
  } else if (serviceTemplateModel.isManual(billing)) {
    return 'Manual';
  } else {
    return 'Automatic';
  }
}
export function occurrenceLabel(billing) {
  if (serviceTemplateModel.isOneTimeInStages(billing)) {
    return 'In stages';
  } else if (serviceTemplateModel.isRecurring(billing)) {
    return recurrencePeriodLabels[billing.recurrencePeriod];
  } else {
    return 'One-time';
  }
}